import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase/client';

export interface BusinessConfig {
  id: string;
  user_id: string;
  biz_name: string;
  owner_name: string;
  contact_email: string;
  contact_phone: string;
  business_type: string;
  service_area: string;
  is_australian_business: boolean;
  abn: string;
  gst_registered: boolean;
  website_url: string;
  instagram?: string;
  facebook?: string;
  bank_account_name: string;
  bank_account_bsb: string;
  bank_account_number: string;
  pricing_mode: 'fixed' | 'quote' | 'both';
  biz_type: string;
  biz_mode: string;
  logo_url?: string;
  license_key?: string;
  onboarding_completed?: boolean;
  domain?: string;
  bank_name?: string;
  payid?: string;
  // Subscription & Monetisation fields
  subscription_status?: 'trial' | 'active' | 'expired' | 'cancelled';
  trial_ends_at?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  whop_membership_id?: string;
  plan?: 'starter' | 'pro';
  ndis_verified?: boolean;
  ndis_discount_applied?: boolean;
  discount_percent?: number; // 0, 50, or 100
  discount_reason?: string; // NDIS verified, referral, beta tester, etc.
}

interface BusinessConfigContextType {
  config: BusinessConfig | null;
  loading: boolean;
  error: string | null;
  refreshConfig: () => void;
}

const BusinessConfigContext = createContext<BusinessConfigContextType>({
  config: null,
  loading: true,
  error: null,
  refreshConfig: () => {},
});

export function BusinessConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<BusinessConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setConfig(null);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('business_config')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        throw new Error('Failed to load config: ' + error.message);
      }

      setConfig(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig, refreshKey]);

  const refreshConfig = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  return (
    <BusinessConfigContext.Provider value={{ config, loading, error, refreshConfig }}>
      {children}
    </BusinessConfigContext.Provider>
  );
}

export const useBusinessConfig = () => {
  const context = useContext(BusinessConfigContext);
  if (!context) {
    throw new Error('useBusinessConfig must be used within a BusinessConfigProvider');
  }
  return context;
};
